import loadable from '@loadable/component';
import { useTracking } from 'react-tracking';
import { RouterLink } from '@redux-first-router-link';

import MoreHorizontalSolid from '@simplywallst/ui-core/icons/MoreHorizontalSolid';
import SupportSolid from '@simplywallst/ui-core/icons/SupportSolid';

import { formatterPercentage } from '@/utilities/format';
import PositiveNegativeValue from '@components/PositiveNegativeValue';

import { useTypeformDrawer } from '@/hooks/useTypeformDrawer';

import type { Company } from './types';
import { CompanyIntrinsicDiscountBase } from '../CompanyIntrinsicDiscount';
import { Item, Menu } from '../Menu';
import { Button } from '@simplywallst/ui-core/next';

const Snowflake = loadable(() => import('@simplywallst/ui-charts/Snowflake'), {
  resolveComponent: (components) => components.Snowflake,
  ssr: false,
});

const SnowflakeDotMarkers = loadable(
  () => import('@simplywallst/ui-charts/Snowflake/components/DotMarkers'),
  {
    resolveComponent: (components) => components.DotMarkers,
    ssr: false,
  }
);

const FeedCardMyFairValue = loadable(() => import('./FeedCardMyFairValue'), {
  resolveComponent: (components) => components.FeedCardMyFairValue,
  ssr: false,
});
interface Props {
  company: Company;
  onClick?: () => void;
}

export const FeedCardHeader = ({ company, onClick }: Props) => {
  const {
    name,
    tickerSymbol,
    scores,
    return7D,
    return1YrAbs,
    sharePriceFormatted,
    canonicalUrl,
    companyExtra,
  } = company;
  const { trackEvent } = useTracking();
  const makeTypeformPopup = useTypeformDrawer('RveL1l', {
    company: name,
  });

  return (
    <div className="relative">
      <RouterLink
        noUnderline
        to={canonicalUrl}
        onClick={onClick}
        className="focus-outline grid grid-cols-[40px_auto] items-start gap-x1_5 rounded-t-x1 bg-gradient-to-b from-p-grey-100 to-p-grey-50 px-x2 py-x1_5 hover:to-p-blue-130/10"
      >
        <Snowflake points={scores || [0, 0, 0, 0, 0]} width={40}>
          <SnowflakeDotMarkers />
        </Snowflake>

        <div className="grid gap-x1">
          <div className="grid pr-[42px]">
            <div className="grid grid-cols-[repeat(2,min-content)_auto] items-center gap-x1">
              <div
                className="text-xs font-strong text-accent"
                data-cy-id="dashboard-feed-card-ticker"
              >
                {tickerSymbol}
              </div>
              <div
                className="text-xs font-strong text-soft"
                data-cy-id="dashboard-feed-card-price"
              >
                {sharePriceFormatted}
              </div>
              {companyExtra?.myValuation?.intrinsicDiscount && (
                <div className="text-tiny">
                  <CompanyIntrinsicDiscountBase
                    discount={companyExtra.myValuation.intrinsicDiscount}
                  />
                </div>
              )}
            </div>
            <div
              className="truncate text-tiny text-softer"
              data-cy-id="dashboard-feed-card-name"
            >
              {name}
            </div>
          </div>

          <div className="grid min-w-0 grid-cols-[min-content_1fr] gap-x1 overflow-hidden min-[375px]:grid-cols-[repeat(2,min-content)_auto]">
            <div className="grid grid-cols-[repeat(2,min-content)] gap-x0_5">
              <span className="text-xs text-softer">7D </span>
              <div className="text-xs">
                <PositiveNegativeValue
                  value={return7D}
                  data-cy-id="dashboard-feed-card-return7D"
                >
                  {formatterPercentage(return7D)}
                </PositiveNegativeValue>
              </div>
            </div>
            <div className="grid grid-cols-[repeat(2,min-content)] gap-x0_5">
              <span className="text-xs text-softer">1Y </span>
              <div className="text-xs">
                <PositiveNegativeValue
                  value={return1YrAbs}
                  data-cy-id="dashboard-feed-card-return1Y"
                >
                  {formatterPercentage(return1YrAbs)}
                </PositiveNegativeValue>
              </div>
            </div>
            <div className="col-span-2 min-[375px]:col-span-1">
              {companyExtra && <FeedCardMyFairValue company={companyExtra} />}
            </div>
          </div>
        </div>
      </RouterLink>

      <div className="absolute right-x1 top-x1">
        <Menu
          label={
            <Button
              variant="subtle"
              aria-label="More options"
              onClick={() => {
                trackEvent({
                  action: 'click',
                  modifier: 'more',
                });
              }}
              size="medium"
              className="!px-x1"
            >
              <MoreHorizontalSolid />
            </Button>
          }
          onAction={(key) => key === 'FEEDBACK' && makeTypeformPopup()}
          selectionMode="none"
        >
          <Item key="FEEDBACK" textValue="Feedback">
            <span
              data-theme="light"
              className="grid grid-cols-[max-content_max-content] items-center gap-x1 fill-soft text-xs text-soft"
            >
              <SupportSolid />
              Feedback
            </span>
          </Item>
        </Menu>
      </div>
    </div>
  );
};
